import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { License } from '@pixacare/domain';
import { FlattenedLicense } from '@pixacare/dtos';
import { Observable } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { LicenseHttpService } from 'src/app/services/http/license.http.service';
import { clientsActions } from '../clients/clients.actions';
import { lifeCycleActions } from '../life-cycle/life-cycle.actions';
import { notificationsActions } from '../notifications/notifications.actions';
import { licensesActions } from './licenses.actions';

@Injectable()
export class LicensesEffects {
  private readonly actions$ = inject(Actions);
  private readonly licenseService = inject(LicenseHttpService);
  private readonly translocoService = inject(TranslocoService);

  loadLicenses$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(licensesActions.loadLicenses),
      mergeMap(() =>
        this.licenseService.getLicenses().pipe(
          switchMap((flattenedLicenses: FlattenedLicense[]) => [
            licensesActions.loadLicensesSuccess({ flattenedLicenses }),
          ]),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  loadLicensesSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(licensesActions.loadLicensesSuccess),
      switchMap(({ flattenedLicenses }) => [
        clientsActions.updateClients({
          clients: flattenedLicenses.map((flattenedLicense) => ({
            ...flattenedLicense.client,
            clientDataConsumption: flattenedLicense.clientDataConsumption,
          })),
        }),
      ]),
      catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
    ),
  );

  answerInvitation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(licensesActions.answerInvitation),
      mergeMap(({ licenseId, accepted }) =>
        this.licenseService.answerInvitation(licenseId, accepted).pipe(
          switchMap((license: License) => [
            licensesActions.answerInvitationSuccess({ license }),
            licensesActions.loadLicenses(),
            notificationsActions.success({
              title: accepted
                ? this.translocoService.translate('Invitation Acceptée')
                : this.translocoService.translate('Invitation Refusée'),
              message: accepted
                ? this.translocoService.translate("Vous avez accepté l'invitation.")
                : this.translocoService.translate("Vous avez refusé l'invitation."),
            }),
          ]),
          catchError((error: Error) => [
            lifeCycleActions.loadError({ error }),
            notificationsActions.error({
              error,
              title: this.translocoService.translate(`Une erreur est survenue.`),
              message: this.translocoService.translate("Nous n'avons pas pu répondre à l'invitation."),
            }),
          ]),
        ),
      ),
    ),
  );
}
